/*
 * What every "paste a token" form of the integrations shares (provider accounts, Bitbucket workspaces): the token
 * check, which API answers mean "this Hangar cannot store tokens yet", and the Idempotency-Key of one create attempt.
 * Pure: no React and nothing that touches `window`.
 */

/** A pasted token shorter than this is a typo, not a token. */
export const MIN_TOKEN_LENGTH = 12;

export const TOKEN_REQUIRED = 'The access token is required (sent once, never shown again)';
export const TOKEN_REQUIRED_ON_REPLACE = 'Paste the new token';
export const TOKEN_LOOKS_WRONG = 'That does not look like a token';

/** Empty or too short; `emptyMessage` is what the form says for an empty field. */
export function tokenFieldError(token: string, emptyMessage: string): string | undefined {
  const t = token.trim();
  if (!t) return emptyMessage;
  return t.length < MIN_TOKEN_LENGTH ? TOKEN_LOOKS_WRONG : undefined;
}

/** `/credentials` missing on this API: an older one answers 404, a newer one 501 `HGR-NOT-IMPLEMENTED`. */
export const credentialsMissingStatus = (status: number): boolean => status === 404 || status === 501;

/** One create attempt: the Idempotency-Key belongs to one body, so a changed body gets a new key (else the API answers 409). */
export interface Attempt {
  body: string;
  key: string;
}

/** The attempt to send `body` with: the previous one when the body did not change, else a new key. */
export function nextAttempt(previous: Attempt | null, body: unknown, newKey: () => string = () => crypto.randomUUID()): Attempt {
  const signature = JSON.stringify(body);
  return previous?.body === signature ? previous : { body: signature, key: newKey() };
}

/** `true` when any field of a validation result carries a message. */
export const hasErrors = (errors: Record<string, string | undefined>): boolean => Object.values(errors).some(Boolean);
